import { Module } from 'vuex'
import { ExamAttempt, ExamUIState } from '@/shared/model/exam.model'
import examService from '@/shared/services/examService'

interface ExamState {
  currentAttempt: ExamAttempt | null
  ui: ExamUIState
  cachedAnswers: Record<string, any> // For local caching before sync
}

const examStore: Module<ExamState, any> = {
  namespaced: true,

  state: {
    currentAttempt: null,
    ui: {
      loading: false,
      submitting: false,
      error: null,
      navigation: {
        currentSectionIndex: 0,
        currentQuestionIndex: 0,
      },
    },
    cachedAnswers: {},
  },

  getters: {
    currentAttempt: (state) => state.currentAttempt,
    currentSection: (state) => {
      if (!state.currentAttempt) return null
      return state.currentAttempt.examProgress[state.ui.navigation.currentSectionIndex]
    },
    currentQuestion: (state, getters) => {
      const section = getters.currentSection
      if (!section) return null
      return section.section.tasks[state.ui.navigation.currentQuestionIndex]
    },
    currentAnswer: (state, getters) => {
      const section = getters.currentSection
      const question = getters.currentQuestion
      if (!section || !question) return null
      return section.answers.find((a: any) => a.question._id === question._id)
    },
    totalQuestions: (state) => {
      if (!state.currentAttempt) return 0
      return examService.getTotalQuestions(state.currentAttempt)
    },
    answeredQuestions: (state) => {
      if (!state.currentAttempt) return 0
      return examService.getAnsweredQuestions(state.currentAttempt)
    },
    isExamComplete: (state) => {
      if (!state.currentAttempt) return false
      return examService.isExamComplete(state.currentAttempt)
    },
    isExamFinished: (state) => {
      if (!state.currentAttempt) return false
      return examService.isExamFinished(state.currentAttempt)
    },
    loading: (state) => state.ui.loading,
    submitting: (state) => state.ui.submitting,
    error: (state) => state.ui.error,
    navigation: (state) => state.ui.navigation,
  },

  mutations: {
    SET_ATTEMPT(state, attempt: ExamAttempt) {
      state.currentAttempt = attempt
      // Load from localStorage if available
      const cached = localStorage.getItem(`exam_${attempt._id}`)
      if (cached) {
        try {
          state.cachedAnswers = JSON.parse(cached)
        } catch (e) {
          console.error('Failed to load cached answers:', e)
        }
      }
    },
    SET_LOADING(state, loading: boolean) {
      state.ui.loading = loading
    },
    SET_SUBMITTING(state, submitting: boolean) {
      state.ui.submitting = submitting
    },
    SET_ERROR(state, error: string | null) {
      state.ui.error = error
    },
    SET_SECTION_INDEX(state, index: number) {
      state.ui.navigation.currentSectionIndex = index
      state.ui.navigation.currentQuestionIndex = 0
    },
    SET_QUESTION_INDEX(state, index: number) {
      state.ui.navigation.currentQuestionIndex = index
    },
    CACHE_ANSWER(state, { sectionId, questionId, answer }) {
      const key = `${sectionId}_${questionId}`
      state.cachedAnswers[key] = answer
      // Save to localStorage
      if (state.currentAttempt) {
        localStorage.setItem(
          `exam_${state.currentAttempt._id}`,
          JSON.stringify(state.cachedAnswers)
        )
      }
    },
    CLEAR_CACHE(state) {
      state.cachedAnswers = {}
      if (state.currentAttempt) {
        localStorage.removeItem(`exam_${state.currentAttempt._id}`)
      }
    },
    RESET(state) {
      state.currentAttempt = null
      state.ui = {
        loading: false,
        submitting: false,
        error: null,
        navigation: {
          currentSectionIndex: 0,
          currentQuestionIndex: 0,
        },
      }
      state.cachedAnswers = {}
    },
  },

  actions: {
    async createAttempt({ commit }, enrollmentId: string) {
      commit('RESET')
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const attempt = await examService.createExamAttempt(enrollmentId)
        commit('SET_ATTEMPT', attempt)
        return attempt
      } catch (error: any) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async loadAttempt({ commit }, attemptId: string) {
      commit('RESET')
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const attempt = await examService.getExamAttempt(attemptId)
        commit('SET_ATTEMPT', attempt)
        return attempt
      } catch (error: any) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async answerQuestion({ commit, state }, { sectionId, questionId, answer }) {
      if (!state.currentAttempt) return

      // Cache locally first
      commit('CACHE_ANSWER', { sectionId, questionId, answer })

      // Prepare request based on answer type
      const request: any = {
        sectionId,
        question: questionId,
      }

      if (Array.isArray(answer)) {
        request.options = answer
      } else {
        request.value = answer ?? ''
      }

      try {
        const updatedAttempt = await examService.answerQuestion(
          state.currentAttempt._id,
          request
        )
        commit('SET_ATTEMPT', updatedAttempt)
        return updatedAttempt
      } catch (error: any) {
        commit('SET_ERROR', error.message)
        throw error
      }
    },

    async submitExam({ commit, state }) {
      if (!state.currentAttempt) return

      commit('SET_SUBMITTING', true)
      commit('SET_ERROR', null)
      try {
        const result = await examService.submitExam(state.currentAttempt._id)
        commit('SET_ATTEMPT', result)
        commit('CLEAR_CACHE')
        return result
      } catch (error: any) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_SUBMITTING', false)
      }
    },

    navigateToSection({ commit }, index: number) {
      commit('SET_SECTION_INDEX', index)
    },

    navigateToQuestion({ commit }, index: number) {
      commit('SET_QUESTION_INDEX', index)
    },

    nextQuestion({ commit, state, getters }) {
      const section = getters.currentSection
      if (!section) return

      const currentQuestionIndex = state.ui.navigation.currentQuestionIndex
      const totalQuestionsInSection = section.section.tasks.length

      if (currentQuestionIndex < totalQuestionsInSection - 1) {
        commit('SET_QUESTION_INDEX', currentQuestionIndex + 1)
      } else {
        // Move to next section
        const currentSectionIndex = state.ui.navigation.currentSectionIndex
        if (state.currentAttempt && currentSectionIndex < state.currentAttempt.examProgress.length - 1) {
          commit('SET_SECTION_INDEX', currentSectionIndex + 1)
        }
      }
    },

    previousQuestion({ commit, state }) {
      const currentQuestionIndex = state.ui.navigation.currentQuestionIndex

      if (currentQuestionIndex > 0) {
        commit('SET_QUESTION_INDEX', currentQuestionIndex - 1)
      } else {
        // Move to previous section
        const currentSectionIndex = state.ui.navigation.currentSectionIndex
        if (currentSectionIndex > 0) {
          commit('SET_SECTION_INDEX', currentSectionIndex - 1)
          // Set to last question of previous section
          if (state.currentAttempt) {
            const prevSection = state.currentAttempt.examProgress[currentSectionIndex - 1]
            commit('SET_QUESTION_INDEX', prevSection.section.tasks.length - 1)
          }
        }
      }
    },

    reset({ commit }) {
      commit('RESET')
    },
  },
}

export default examStore
